import { useLazyGetAccountHolderQuery } from "@/modules/account/services";
import type { IAccountHolder } from "@/modules/transaction/common/types";
import { ErrorMessages } from "@/shared/common/enums";
import type { TSetError } from "@/shared/common/types";
import { isAmeriaAccount, isValidAccountChecksum } from "@/shared/helpers";
import { type Dispatch, type SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

interface IGetAccountInfoProps {
  account: string;
  creditAccountKey: string;
  beneficiaryNameKey: string;
  getBeneficiaryName: () => string;
  setValue: (key: string, value: string) => void;
  setError: TSetError;
  clearErrors?: (key: string) => void;
}

interface Props {
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const useGetAccountHolder = ({ setIsAccountNumberInvalid }: Props) => {
  const { t } = useTranslation();
  const [accountData, setAccountData] = useState<IAccountHolder | null>(null);

  const [getAccountHolder] = useLazyGetAccountHolderQuery();

  const getAccountInfo = ({
    account,
    setValue,
    setError,
    clearErrors,
    getBeneficiaryName,
    creditAccountKey,
    beneficiaryNameKey,
  }: IGetAccountInfoProps) => {
    if (!isValidAccountChecksum(account)) {
      setAccountData(null);
      setValue(beneficiaryNameKey, "");
      return;
    }
    setIsAccountNumberInvalid(false);
    getAccountHolder(account)
      .unwrap()
      .then((data) => {
        setAccountData(data);
        if (isAmeriaAccount(account)) {
          if (!data?.holder) {
            setError?.(beneficiaryNameKey, {
              message: t(ErrorMessages.BeneficiaryWrongAccount)
            });
          } else {
            setValue(beneficiaryNameKey, data.holder);
          }
        } else {
          setValue(beneficiaryNameKey, getBeneficiaryName());
        }
        clearErrors?.(beneficiaryNameKey);
        clearErrors?.(creditAccountKey);
      })
      .catch(() => {
        setAccountData(null);
        setIsAccountNumberInvalid(true);
        setError?.(
          creditAccountKey,
          { message: t(ErrorMessages.UnknownAccountHolder) }
        );
      });
  };
  const clearAccountData = () => {
    setAccountData(null);
    setIsAccountNumberInvalid(false);
  };

  return { accountData, getAccountInfo, clearAccountData };
};
